import { gql } from '@apollo/client';

/**
 * AOI 详情 - 基础信息 + 衍生属性 + 关联地址 / 子 AOI + 几何 (含周边 500m 内的 AOI)。
 *
 * <p>attribute.accessCodes 是 AOI 自己的门禁 (存 biz_aoi_attribute.access_codes),
 * 跟进门点 / 停车点同源, 元素身份是服务端重算的 uid。
 */
export const GET_AOI_ATTRIBUTE_DETAIL = gql`
  query GetAoiAttributeDetail($aoiId: Long!, $zipcode: String!) {
    getAoiAttributeDetail(aoiId: $aoiId, zipcode: $zipcode) {
      aoiId
      level
      centerLatitude
      centerLongitude
      zipcode
      parentAoiId
      displayName
      houseNumber
      dataSource
      status
      aoiUpdatedAt
      aoiCreatedAt
      attribute {
        entryPoints {
          lng
          lat
          remark
          uid
        }
        parkingPoints {
          lng
          lat
          remark
          uid
        }
        accessCodes {
          id
          uid
          codeType
          codeValue
          available
          remark
        }
        deliveryTime
        typeCode
        typeCategoryCode
        typeZhPath
        aoiSourceId
        dataSource
        updatedAt
        bizTags {
          uid
          tag
          zhName
          kind
          paramsJson
          coverZipcodes
          source
          remark
          version
        }
      }
      relatedAddresses {
        addressCoreId
        fullStandardAddress
        latitude
        longitude
        entryPoints {
          lng
          lat
          remark
          uid
        }
        parkingPoints {
          lng
          lat
          remark
          uid
        }
      }
      childAois {
        aoiId
        displayName
        centerLatitude
        centerLongitude
        geometryGeoJson
      }
      selfGeometry {
        aoiId
        geometryGeoJson
        centerLatitude
        centerLongitude
        minLatitude
        minLongitude
        maxLatitude
        maxLongitude
      }
      nearbyGeometries {
        aoiId
        geometryGeoJson
        centerLatitude
        centerLongitude
        distanceMeters
      }
    }
  }
`;

export interface BizAoiAttributeLocationPoint {
  lng: number | null;
  lat: number | null;
  remark: string | null;
  /** 要素级确定性 uid (服务端权威生成); 反馈/点赞类需求以此引用具体点位 */
  uid?: string | null;
}

export interface BizAoiAttributeAccessCode {
  /** 行 id(biz_access_code 主键), 编辑保存时原样回传; 服务端按它精确识别"改的是哪一行" */
  id: string | null;
  /** 要素级确定性 uid (服务端按 归属 + 类型 + 码值 重算; 改码值 = 换身份) */
  uid: string | null;
  codeType: string | null;
  codeValue: string | null;
  /** DB 枚举名: PENDING_VERIFICATION / AVAILABLE / UNAVAILABLE */
  available: string | null;
  remark: string | null;
}

/** AOI 业务标签 (biz_aoi_attribute.biz_tags 一项; uid / kind / coverZipcodes / version 由服务端算)。 */
export interface BizAoiBizTag {
  uid: string | null;
  tag: string;
  /** BFF 按枚举派生的中文名; 字典里没有的旧 code 回落为 code */
  zhName: string;
  kind: string | null;
  paramsJson: string | null;
  /** 围栏覆盖的邮编 (打标时算好, 只读) */
  coverZipcodes: string[];
  source: string | null;
  remark: string | null;
  version: number | null;
}

export interface BizAoiAttributeData {
  entryPoints: BizAoiAttributeLocationPoint[];
  parkingPoints: BizAoiAttributeLocationPoint[];
  accessCodes: BizAoiAttributeAccessCode[];
  deliveryTime: string | null;
  typeCode: string | null;
  typeCategoryCode: string | null;
  typeZhPath: string | null;
  aoiSourceId: string | null;
  dataSource: number | null;
  updatedAt: string | null;
  /** 业务标签; 老接口不带时可能为 undefined, 用 ?? [] 兜底 */
  bizTags?: BizAoiBizTag[] | null;
}

export interface BizAoiAttributeRelatedAddress {
  addressCoreId: number;
  fullStandardAddress: string | null;
  latitude: number | null;
  longitude: number | null;
  /** 该地址自己的地址级进门点 (实采精确数据, 与楼级 attribute.entryPoints 是两层) */
  entryPoints: BizAoiAttributeLocationPoint[];
  /** 该地址自己的地址级停车点 */
  parkingPoints: BizAoiAttributeLocationPoint[];
}

export interface BizAoiAttributeChildAoi {
  aoiId: number;
  displayName: string | null;
  centerLatitude: number | null;
  centerLongitude: number | null;
  geometryGeoJson: string | null;
}

export interface BizAoiAttributeGeometry {
  aoiId: number | null;
  geometryGeoJson: string | null;
  centerLatitude: number | null;
  centerLongitude: number | null;
  minLatitude?: number | null;
  minLongitude?: number | null;
  maxLatitude?: number | null;
  maxLongitude?: number | null;
  distanceMeters?: number | null;
}

export interface BizAoiAttributeDetail {
  aoiId: number;
  level: number | null;
  centerLatitude: number | null;
  centerLongitude: number | null;
  zipcode: string | null;
  parentAoiId: number | null;
  displayName: string | null;
  /** 建筑大号 (biz_aoi.house_number, 楼本体门牌主干); null=无 */
  houseNumber: string | null;
  /** AOI 本体数据来源: SURVEY=专业实采 / OVERTURE / OSM 等 */
  dataSource: string | null;
  status: number | null;
  aoiUpdatedAt: string | null;
  /** biz_aoi 主表创建时间 (ISO-8601); 抽屉"创建时间"展示用 */
  aoiCreatedAt: string | null;
  attribute: BizAoiAttributeData | null;
  relatedAddresses: BizAoiAttributeRelatedAddress[];
  childAois: BizAoiAttributeChildAoi[];
  selfGeometry: BizAoiAttributeGeometry | null;
  nearbyGeometries: BizAoiAttributeGeometry[];
}

export interface GetAoiAttributeDetailResponse {
  getAoiAttributeDetail: BizAoiAttributeDetail;
}
